import { useApp } from "@/contexts/app-context";
import { useAuth } from "@/features/auth/hooks/useAuthContext";
import { API_BASE_URL } from "@/lib/api-config";
import {
    archiveConversation,
    createConversation,
    deleteConversation,
    getConversation,
    getConversations,
    unarchiveConversation,
    updateConversationName,
} from "@/services/supabase/conversations";
import { getAssistantMessages } from "@/services/supabase/messages";
import { useChat } from "@ai-sdk/react";
import {
    AssistantRuntimeProvider,
    unstable_RemoteThreadListAdapter as RemoteThreadListAdapter,
    useAuiState,
    unstable_useRemoteThreadListRuntime as useRemoteThreadListRuntime,
} from "@assistant-ui/react";
import { AssistantChatTransport, useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import { createAssistantStream } from "assistant-stream";
import { ReactNode, useEffect, useMemo, useState } from "react";


function extractLastUserMessageText(unstable_messages: readonly any[]): string {
    for (let i = unstable_messages.length - 1; i >= 0; i--) {
        const m = unstable_messages[i];
        if (m?.role === "user") {
            // Handle plain string content (common in AI SDK)
            if (typeof m.content === "string") return m.content;

            // Handle structured content parts (common in assistant-ui/modern AI SDK)
            const parts = m.parts || m.content;
            if (Array.isArray(parts)) {
                return parts
                    .map((c: any) => (typeof c?.text === "string" ? c.text : ""))
                    .join(" ")
                    .trim();
            }
        }
    }
    return "";
}

// Helper: derive a concise title from the user text
function titleFromText(text: string, maxWords = 6): string {
    console.log("Generating title from text:", text);
    const trimmed = text.trim();
    // Prefer the first sentence if present
    const firstSentence = trimmed.match(/[^.!?]+[.!?]?/)?.[0]?.trim() ?? trimmed;

    // Cap by word count
    const words = firstSentence.split(/\s+/).slice(0, maxWords);
    let t = words.join(" ").trim();

    // Simple capitalization and length cap
    if (t.length > 90) t = t.slice(0, 87).trim() + "…";
    if (t.length === 0) t = "Conversation";

    // Capitalize first letter
    return t.charAt(0).toUpperCase() + t.slice(1);
}

export function ProjectChatGuard({ children }: { children: ReactNode }) {
    const {
        storyId: activeStoryId,
        projectId: activeProjectId,
        tenantId,
        isLoading,
        isStoryLoading
    } = useApp();

    if (isLoading || isStoryLoading || !tenantId) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="text-muted-foreground animate-pulse">Loading context...</div>
            </div>
        );
    }

    if (!activeProjectId) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="text-muted-foreground">Select a project to start chatting.</div>
            </div>
        );
    }

    if (!activeStoryId) {
        return (
            <div className="flex h-full w-full items-center justify-center flex-col gap-2">
                <div className="text-muted-foreground text-center">
                    <p>This project has no stories yet.</p>
                    <p className="text-sm">Create a story in the project overview to start chatting.</p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}

export function EchoRuntimeProvider({
    children,
}: Readonly<{ children: ReactNode }>) {
    const { tenantId, storyId, projectId } = useApp();
    const { isAuthenticated } = useAuth();

    // If not logged in or still waiting for tenantId, just render children
    // (This allows the global AppRoutes guard to show the splash screen)
    if (!isAuthenticated || !tenantId) {
        return <>{children}</>;
    }

    return <EchoRuntimeInternal key={`${storyId}-${projectId}`}>{children}</EchoRuntimeInternal>;
}

function EchoRuntimeInternal({ children }: { children: ReactNode }) {
    const {
        storyId: activeStoryId,
        projectId: activeProjectId,
        tenantId: activeTenantId,
        chapterId: activeChapterId,
    } = useApp();
    const { session } = useAuth();

    // Thread mapping state
    const [threadMapping, setThreadMapping] = useState<Record<string, string>>({});

    console.log("[AIChat] Internal Provider Render - tenantId:", activeTenantId);

    const onNewThread = (localId: string, remoteId: string) => {
        if (!remoteId || remoteId.startsWith("__LOCALID_")) return;
        setThreadMapping(prev => ({ ...prev, [localId]: remoteId }));
    };


    const threadListAdapter = useMemo((): RemoteThreadListAdapter => ({
        async list() {
            if (!activeStoryId) return { threads: [] };

            console.log("Listing conversations for story and project:", activeStoryId, activeProjectId);
            const threads = await getConversations(activeStoryId, activeProjectId!);
            return {
                threads: threads.map((t: any) => ({
                    id: t.conversation_id,
                    remoteId: t.conversation_id,
                    status: t.status === "archived" ? "archived" : "regular",
                    title: t.name,
                })),
            };
        },

        async initialize(localId) {
            console.log("Initializing conversation for localId:", localId);
            if (!activeStoryId) throw new Error("No active story selected");

            console.log("Creating conversation for story and project:", activeStoryId, activeProjectId);
            const conversation = await createConversation(activeStoryId, localId, activeProjectId!);

            if (localId) {
                onNewThread(localId, conversation.conversation_id);
            }

            return {
                id: localId,
                remoteId: conversation.conversation_id,
                externalId: undefined
            };
        },

        async rename(remoteId, title) {
            await updateConversationName(remoteId, title);
        },

        async archive(remoteId) {
            await archiveConversation(remoteId);
        },

        async unarchive(remoteId) {
            await unarchiveConversation(remoteId);
        },

        async delete(remoteId) {
            console.log("deleting the remote id", remoteId)
            await deleteConversation(remoteId);
        },

        async fetch(remoteId) {
            const conversation = await getConversation(remoteId);
            return {
                id: remoteId,
                remoteId: conversation!.conversation_id,
                status: conversation!.status === "archived" ? "archived" : "regular",
                title: conversation!.name || undefined,
            };
        },

        async generateTitle(_remoteId, messages) {
            const userText = extractLastUserMessageText(messages);
            if (!userText) {
                return createAssistantStream(async (controller) => {
                    controller.appendText("New Conversation");
                });
            }

            const title = titleFromText(userText);

            // Update local state if rename is available
            this.rename(_remoteId, title).catch(err =>
                console.error("Failed to rename conversation:", err)
            );

            return createAssistantStream(async (controller) => {
                controller.appendText(title);
            });
        },
    }), [activeStoryId, activeProjectId]);

    function useThreadRuntime() {
        const thread = useAuiState((s) => s.threadListItem);
        const localId = thread?.id;

        // // Effective ID is either the mapped remote ID or the current remote ID or the local ID
        const effectiveRemoteId = (localId && threadMapping[localId]) || thread?.remoteId || localId;

        const api = `${API_BASE_URL}/api/chat/session`;
        console.log("[AIChat] Thread Runtime Render - tenantId:", activeTenantId, activeStoryId, activeProjectId, activeChapterId);

        const transport = useMemo(() => new AssistantChatTransport({
            api,
            headers: {
                Authorization: `Bearer ${session?.access_token}`,
                "x-story-id": String(activeStoryId || ""),
                "x-project-id": String(activeProjectId || ""),
                "x-tenant-id": String(activeTenantId || ""),
                "x-chapter-id": String(activeChapterId || ""),
            },
            body: {
                conversationId: effectiveRemoteId,
            }
        }), [api, session?.access_token, activeStoryId, activeProjectId, activeTenantId, activeChapterId, effectiveRemoteId]);

        const chat = useChat({
            id: localId,
            transport: transport as any,
        } as any);

        const [lastFetchedId, setLastFetchedId] = useState<string | null>(null);

        useEffect(() => {
            if (!effectiveRemoteId || effectiveRemoteId.startsWith("__LOCALID_")) {
                if (lastFetchedId !== effectiveRemoteId) {
                    chat.setMessages([]);
                    setLastFetchedId(effectiveRemoteId);
                }
                return;
            }

            if (lastFetchedId === effectiveRemoteId) return;

            let active = true;
            console.log("[AIChat] Fetching messages for:", effectiveRemoteId);
            getAssistantMessages(effectiveRemoteId).then((msgs) => {
                if (!active) return;

                console.log("[AIChat] Setting messages:", msgs);

                // Track if we're swapping from local to remote for same thread
                const wasLocal = lastFetchedId?.startsWith("__LOCALID_");
                const isRemote = !effectiveRemoteId.startsWith("__LOCALID_");

                // If just initialized, let the existing chat messages stay if DB returned nothing
                if (wasLocal && isRemote && (!msgs || msgs.length === 0)) {
                    console.log("[AIChat] Skipped setting empty messages during remote transition");
                } else {
                    chat.setMessages(msgs as any);
                }
                setLastFetchedId(effectiveRemoteId);
            }).catch(err => {
                console.error("[AIChat] Failed to load messages:", err);
            });

            return () => { active = false; };
        }, [effectiveRemoteId, lastFetchedId, chat.setMessages]);

        const runtime = useAISDKRuntime(chat);

        useEffect(() => {
            transport.setRuntime(runtime);
        }, [transport, runtime]);

        return runtime;
    }

    const runtime = useRemoteThreadListRuntime({
        runtimeHook: useThreadRuntime,
        adapter: threadListAdapter,
    });

    return (
        <AssistantRuntimeProvider runtime={runtime}>
            {children}
        </AssistantRuntimeProvider>
    );
}
